import React from "react";
import { format } from "date-fns";
import { FileText, MoveRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MenubarItem } from "@/components/ui/menubar";
import { TruncatedText } from "@/components/ui/truncated-text";

interface JourneyMenuItemProps {
  journey: {
    id: string;
    name: string;
    timestamp: string;
    folderId: string | null;
  };
  onLoad: (id: string) => void;
  onMove: (journeyId: string, folderId: string | null) => void;
  folders: Array<{ id: string; name: string; }>;
  showFolderInfo?: boolean;
}

const JourneyMenuItem: React.FC<JourneyMenuItemProps> = ({ 
  journey, 
  onLoad, 
  onMove,
  folders,
  showFolderInfo = true 
}) => {
  return (
    <MenubarItem 
      key={journey.id} 
      onClick={() => onLoad(journey.id)}
      className="flex items-center justify-between group hover:bg-blue-200/50"
    >
      <div className="flex-1 min-w-0 mr-1">
        <div className="font-medium flex items-center min-w-0">
          <FileText className="mr-1.5 h-4 w-4 text-muted-foreground flex-shrink-0" />
          <TruncatedText 
            text={journey.name} 
            maxWidth="[160px]"
            tooltipSide="right"
            tooltipAlign="start"
          />
        </div>
        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
          {format(new Date(journey.timestamp), "MMM d, yyyy h:mm a")}
        </div>
      </div>
      <div className="flex items-center flex-shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-6 w-6 flex-shrink-0">
              <MoveRight className="h-3 w-3 text-muted-foreground" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[160px] max-w-[220px]">
            <DropdownMenuItem 
              className="flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                onMove(journey.id, null);
              }}
            >
              <span className="truncate">Move to Root</span>
            </DropdownMenuItem>
            {folders.map(folder => (
              <DropdownMenuItem 
                key={folder.id}
                className="flex items-center"
                onClick={(e) => {
                  e.stopPropagation();
                  onMove(journey.id, folder.id);
                }}
                disabled={journey.folderId === folder.id}
              >
                <div className="truncate">Move to <TruncatedText text={folder.name} className="inline-block" maxWidth="[100px]" /></div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </MenubarItem>
  );
};

export default JourneyMenuItem;
